import { urls } from '@/urls';
import { useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { Box, Button, TextField, Autocomplete, Stack } from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { AppCard } from '@/components/app-card/app-card';
import { useGlobalBudget } from '@/screens/budgets/components/hooks/useGlobalBudget';
import { Loader } from '@/components/loader/loader';
import AnnualBudgetConsumptionChart from '../charts/annual-budget-consumption-chart';
import MonthlyBudgetConsumptionChart from '../charts/monthly-budget-consumption-chart';
import { BudgetChangeModal, BudgetChangeData } from './budget-change-modal';
import { getUser } from '@/lib/common';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { formatCurrencyNoUnit } from '@/lib/formatters';

export default function GlobalBudget() {
  const {
    globalBudget: globalBudgetData,
    loading: globalBudgetLoading,
    refetch: refetchGlobalBudget,
  } = useGlobalBudget();
  const [globalBudget, setGlobalBudget] = useState<number>();
  const [currencies, setCurrencies] = useState<string[]>();
  const [budgetCurrency, setBudgetCurrency] = useState<string>('');
  const [additionalDataLoading, setAdditionalDataLoading] = useState(true);
  const [showBudgetChangeModal, setShowBudgetChangeModal] = useState(false);
  const [departmentsBudgetsSum, setDepartmentsBudgetsSum] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const user = getUser();

  const handleBudgetChangeClick = () => {
    setShowBudgetChangeModal(true);
  };

  const handleBudgetChangeSubmit = async (data: BudgetChangeData) => {
    try {
      await fetchWrapper.post(urls.DevOps.createGlobalBudgetChangeRequest, {
        currency: budgetCurrency,
        newBudgetValue: data.newAnnualBudget,
        reason: data.reason,
        requesterId: user?.id,
      });

      await refetchGlobalBudget();
      setRefreshTrigger((prev) => prev + 1);
      setShowBudgetChangeModal(false);
    } catch (error) {
      console.error('Error processing budget change:', error);
    }
  };

  useEffect(() => {
    if (globalBudgetData) {
      setGlobalBudget(globalBudgetData.original_currency_value);
      setBudgetCurrency(globalBudgetData.original_currency);
    } else {
      setBudgetCurrency(user?.default_currency || 'USD');
    }
  }, [globalBudgetData, user]);

  useEffect(() => {
    const fetchAdditionalData = async () => {
      setAdditionalDataLoading(true);
      try {
        const [currencyData, departmentsSumData] = await Promise.all([
          fetchWrapper.get(urls.DevOps.currencies),
          fetchWrapper.get(
            `${urls.DevOps.getDepartmentsBudgetsSum}?currency=${
              budgetCurrency || 'USD'
            }`,
          ),
        ]);

        setCurrencies(currencyData.map((x: any) => x.currency));
        setDepartmentsBudgetsSum(departmentsSumData.sum || 0);
      } catch (error) {
        console.error('Error fetching additional data:', error);
      } finally {
        setAdditionalDataLoading(false);
      }
    };

    fetchAdditionalData();
  }, [budgetCurrency]);

  if (globalBudgetLoading || additionalDataLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      <AppCard
        className="card100"
        title="Global Budget"
        icon={<BarChartOutlinedIcon color="primary" />}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
          }}>
          <TextField
            id="system-value"
            label="Annual Global Budget"
            variant="outlined"
            disabled={globalBudget !== null}
            size="small"
            value={
              globalBudget ? formatCurrencyNoUnit(globalBudget) : ''
            }
            onChange={(e) => setGlobalBudget(Number(e.target.value))}
          />

          <Autocomplete
            id="combo-box-currencies"
            disablePortal
            getOptionLabel={(option: any) => option}
            options={currencies ?? []}
            disabled={globalBudget !== null}
            value={budgetCurrency ?? null}
            onChange={(e, value) => {
              if (value) {
                setBudgetCurrency(value);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Budget Currency"
                variant="outlined"
                size="small"
                sx={{ width: 150 }}
              />
            )}
          />

          {user?.is_sponsor && (
            <Button
              variant="contained"
              size="small"
              onClick={handleBudgetChangeClick}
              color={globalBudget ? 'error' : 'primary'}>
              {globalBudget !== null && globalBudget !== undefined
                ? 'Global Budget Change'
                : 'Set Global Budget'}
            </Button>
          )}
        </Box>
      </AppCard>

      <AppCard
        className="card100"
        title="Annual budget consumption"
        icon={<BarChartOutlinedIcon color="primary" />}
        headerRight={<CurrencyToggle budgetCurrency={globalBudgetData?.original_currency} />}>
        <AnnualBudgetConsumptionChart
          budgetType="global"
          refreshTrigger={refreshTrigger}
          budgetCurrency={globalBudgetData?.original_currency}
        />
      </AppCard>

      <AppCard
        className="card100"
        title="Monthly budget consumption"
        icon={<BarChartOutlinedIcon color="primary" />}
        headerRight={<CurrencyToggle budgetCurrency={globalBudgetData?.original_currency} />}>
        <MonthlyBudgetConsumptionChart
          budgetType="global"
          refreshTrigger={refreshTrigger}
          budgetCurrency={globalBudgetData?.original_currency}
        />
      </AppCard>

      <BudgetChangeModal
        open={showBudgetChangeModal}
        onClose={() => setShowBudgetChangeModal(false)}
        onSubmit={handleBudgetChangeSubmit}
        budgetType="global"
        currentBudget={globalBudgetData?.original_currency_value || 0}
        budgetCurrency={globalBudgetData?.original_currency ?? budgetCurrency}
        departmentsBudgetsSum={departmentsBudgetsSum}
      />
    </Stack>
  );
}
